import { CheckboxField } from '@aws-amplify/ui-react';
import { useEffect, useState } from 'react';
import type { ExportOptionsProps } from './ContactExportOptions';
import { ExportRangeSelector } from './ExportRangeSelector';
import { EXPORT_NOTIFICATIONS_DISABLED, EXPORT_NOTIFICATIONS_DISABLED_MESSAGE } from './exportNotifications';

interface InstitutionExportOptionsProps extends ExportOptionsProps {
  isCrmTarget: boolean;
  /** Built from a HubSpot match run, whose institutions are fixed by the match itself. */
  isFromMatchRun?: boolean;
}

export function InstitutionExportOptions({ value, onChange, target, disabled, isCrmTarget, isFromMatchRun, estimatedCount, rangeFrom, rangeTo, onRangeChange, onRangeValidChange }: InstitutionExportOptionsProps) {
  const [sendNotification, setSendNotification] = useState<boolean>(value.sendNotification ?? false);
  const [includeChildInstitutions, setIncludeChildInstitutions] = useState<boolean>(value.includeChildInstitutions ?? false);
  const [preserveParentChildRelationship, setPreserveParentChildRelationship] = useState<boolean>(value.preserveParentChildRelationship ?? false);

  // Sync options to parent whenever any value changes
  useEffect(() => {
    // Forced off while disabled, so re-saving an older export cannot leave it asking for a
    // notification that will never arrive.
    const options: Record<string, any> = {
      sendNotification: EXPORT_NOTIFICATIONS_DISABLED ? false : sendNotification
    };
    if (isCrmTarget) {
      options.includeChildInstitutions = includeChildInstitutions;
      options.preserveParentChildRelationship = preserveParentChildRelationship;
    }
    onChange(options);
  }, [sendNotification, includeChildInstitutions, preserveParentChildRelationship, isCrmTarget]);

  return (
    <>
      {/* Hidden for a match run: every institution here came from matching a company the
          customer already has, and their schools were never matched, so pulling children in
          would create new records rather than enrich existing ones. The server rejects it
          too - there is nothing to decide, so there is nothing to show. */}
      {isCrmTarget && !isFromMatchRun && (
        <div className="export-step__section">
          <h3 className="export-step__section-heading">Institution Hierarchy</h3>

          <CheckboxField
            label="Include child institutions"
            name="includeChildInstitutions"
            checked={includeChildInstitutions}
            onChange={(e) => setIncludeChildInstitutions(e.target.checked)}
            isDisabled={disabled}
          />
          <div className="desc-2" style={{ marginLeft: 24, marginBottom: 15 }}>
            When exporting a district, also export all schools within that district.
          </div>

          {(target === 'SALESFORCE' || target === 'HUBSPOT') && includeChildInstitutions && (
            <>
              <CheckboxField
                label={`Preserve parent-child relationship in ${target === 'SALESFORCE' ? 'Salesforce' : 'HubSpot'}`}
                name="preserveParentChildRelationship"
                checked={preserveParentChildRelationship}
                onChange={(e) => setPreserveParentChildRelationship(e.target.checked)}
                isDisabled={disabled}
              />
              <div className="desc-2" style={{ marginLeft: 24 }}>
                Link schools to their parent district in the CRM to maintain organizational hierarchy.
              </div>
            </>
          )}
        </div>
      )}

      {/* Record Range Section */}
      {onRangeChange && (
        <ExportRangeSelector
          estimatedCount={estimatedCount ?? 0}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          onChange={onRangeChange}
          onValidChange={onRangeValidChange}
          disabled={disabled}
        />
      )}

      <div className="export-step__section">
        <h3 className="export-step__section-heading">Notifications</h3>
        {/* The wrapper carries the tooltip: a disabled input fires no mouse events of its own. */}
        <span
          data-tooltip-id={EXPORT_NOTIFICATIONS_DISABLED ? 'main-tooltip' : undefined}
          data-tooltip-content={EXPORT_NOTIFICATIONS_DISABLED ? EXPORT_NOTIFICATIONS_DISABLED_MESSAGE : undefined}
          style={EXPORT_NOTIFICATIONS_DISABLED ? { cursor: 'help', display: 'inline-block' } : undefined}
        >
          <CheckboxField
            label="Send me an email when the export completes"
            name="sendNotification"
            checked={EXPORT_NOTIFICATIONS_DISABLED ? false : sendNotification}
            onChange={(e) => setSendNotification(e.target.checked)}
            isDisabled={disabled || EXPORT_NOTIFICATIONS_DISABLED}
          />
        </span>
      </div>
    </>
  );
}
